import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import * as moment from 'moment';

import { SessionService } from './services/session.service';
import { MessageService } from './services';
import { AppConfig } from './services/app-config';
import { Config } from './interfaces';
import { ConfigurationConstants } from './constants/config';
import { UserRoles } from './constants';
import { map } from 'rxjs/operators';

const checkAuthExpriationInteval = 1000 * 60 * 1;// 1 minute
const expiresSoonPeriod = 1000 * 60 * 20; // 20 minutes

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  baseUrl: string;
  authUrl: string;
  principleUrl: string;
  loginStackUrl: string;
  exacteraDomainUrl: string;
  xbsEnvironmentShort: string;
  loginAttempts: number;
  refreshAttempts: number;
  lastRefreshedOn: moment.Moment;
  refreshTimerHandle: NodeJS.Timer;
  hostname: string;

  constructor(
    protected _http: HttpClient,
    protected _router: Router,
    protected _sessionService: SessionService,
    protected _messageService: MessageService,
    protected _config: AppConfig) {
    this.baseUrl = this._config.getBaseApiUrl();
    this.authUrl = this._config.getAuthUrl();
    this.principleUrl = this._config.getPrincipleUrl();
    this.loginStackUrl = `${window.location.origin}/${this._config.getLoginStackRelativeUrl()}`;
    this.exacteraDomainUrl = `${this._config.getExacteraDomainUrl()}`;
    this.xbsEnvironmentShort = `${this._config.getEnvironmentShort()}`;

    this.hostname = window.location.hostname;

    this.loginAttempts = 0;
    this.refreshAttempts = 0;
    if (this.isLoggedIn()) {
      this.lastRefreshedOn = moment();
      this.startAuthRefreshTimer();
    }
  }

  public onLoginSuccess(): void {
    this.lastRefreshedOn = moment();
    if (!this.isAuthRefreshTimerRunning()) {
      this.startAuthRefreshTimer();
    }
    this._sessionService.resetRedirctFromLoginFlag();
    this._sessionService.setUserDetails();
    this._sessionService.setUserIdForWalkMe();
  };

  public login(): void {
    if (this.hostname === 'localhost' || this.hostname.includes('tpiqlocal') || this.hostname.includes('exactera')) {
      window.location.replace(this.loginStackUrl);
    } else {
      const redirectUrl =
        this.xbsEnvironmentShort === 'prod' ?
          `https://tp.${this.exacteraDomainUrl}` :
          `https://tp.${this.xbsEnvironmentShort}.${this.exacteraDomainUrl}`;
      window.location.replace(redirectUrl);
    }
  }

  public changeOldPassword(oldPassword: string, newPassword: string): Observable<any> {
    const userDetails = {
      userName: this._sessionService.getUserDetails().username,
      oldPassword,
      newPassword,
      authSession: {
        idToken: this._sessionService.getAuthToken(),
        accessToken: this._sessionService.getAccessToken()
      }
    };
    return this._http.post<any>(`${this.authUrl}changeOldPassword`, userDetails);
  }

  public setup2fa(): Observable<any> {
    const mfaData = {
      authSession: {
        idToken: this._sessionService.getAuthToken(),
        accessToken: this._sessionService.getAccessToken()
      }
    };
    return this._http.post<any>(`${this.authUrl}generateMfaCode`, mfaData);
  }

  public verify2fa(totpCode: string): Observable<any> {
    const mfaData = {
      totpCode,
      authSession: {
        idToken: this._sessionService.getAuthToken(),
        accessToken: this._sessionService.getAccessToken()
      }
    };
    return this._http.post<any>(`${this.authUrl}verifyMfaTempPassword`, mfaData);
  }

  public disable2fa(): void {
    const mfaData = {
      authSession: {
        idToken: this._sessionService.getAuthToken(),
        accessToken: this._sessionService.getAccessToken()
      }
    };
    this._http.post<any>(`${this.authUrl}disableMfa`, mfaData).subscribe(
      () => {
        this._messageService.displayMessage({ body: 'global.success_disable_2fa', type: 'success' });
        this.logout();
      }
    );
  }

  public home(): void {
    
    const envShort = this._config.getEnvironmentShort();
    const exacteraDomainUrl = this._config.getExacteraDomainUrl();
    const hostname = window.location.hostname;
    
    const redirectUrl =
      envShort === 'prod' ?
        `https://tp.${exacteraDomainUrl}` :
        `https://tp.${envShort}.${exacteraDomainUrl}`;

    if (hostname.includes('xbscompliance')) {
      window.location.replace(redirectUrl);
    }

    const userRole = this._sessionService.getUserRole();
    let homeRoute;

    if (UserRoles.COMPLIANCE_ONLY_ROLES[userRole]) {
      homeRoute = '/central/compliance';
      this._sessionService.setWorkspaceChoice(ConfigurationConstants.COMPLIANCE_CENTRAL_WORKSPACE_CHOICE);
    } else {
      homeRoute = '/start-up-menu';
    }

    if (this.isEulaAccepted()) {
      this._router.navigate([homeRoute]);
    } else {
      this._router.navigate(['/eula']);
    }
  }

  public async logout() {
    if (this._sessionService.sessionExists()) {
      await this._http.get<any>(`${this.authUrl}logout`).toPromise();
    }
    this._sessionService.emptySession();
    this._sessionService.setUserIdForWalkMe();
    this.stopAuthRefreshTimer();
    window.location.replace(this.loginStackUrl);
  }

  public isLoggedIn(): boolean {
    const isLoggedIn = moment().isBefore(this.getExpiration());
    if (!isLoggedIn && this.isAuthRefreshTimerRunning()) {
      this.stopAuthRefreshTimer();
    }
    return isLoggedIn;
  }

  public isEulaAccepted(): boolean {
    const licenseAgreementAccepted = this._sessionService.getUserEula();
    return parseInt(licenseAgreementAccepted, 8) === 1;
  }

  public isLoggedOut(): boolean {
    return !this.isLoggedIn();
  }

  public isRedirectFromLogin(): boolean {
    return this._sessionService.getRedirctFromLoginFlag() === 'true';
  }

  willExpireSoon(): boolean {
    return this.getExpiration().diff(moment(), 'milliseconds', true) < expiresSoonPeriod;
  }

  public getExpiration(): moment.Moment {
    const expiresAt = this._sessionService.getAuthExpiration();
    return moment.unix(expiresAt);
  }

  public refreshSession(callback: any): void {
    const refreshToken = this._sessionService.getRefreshToken();

    if (!refreshToken) {
      this.logout();
      return callback(null);
    } else {
      this._http.post<any>(`${this.authUrl}refresh`, { refreshToken })
        .subscribe(
          refreshRes => {
            this._sessionService.setAuthResult(refreshRes.data.idToken, refreshRes.data.accessToken);
            return callback(null);
          },
          err => callback(err)
        );
    }
  }

  startAuthRefreshTimer(): void {
    this.refreshTimerHandle = setTimeout(() => {
      if (this.lastRefreshedOn.isBefore(moment(), 'day')) {
        this.logout();
      } else {
        if (this.willExpireSoon()) {
          this.refreshSession((err) => {
            if (err) {
              this.logout();
            } else {
              this.lastRefreshedOn = moment();
            }
          });
        }
        this.startAuthRefreshTimer();
      }
    }, checkAuthExpriationInteval);
  }

  stopAuthRefreshTimer(): void {
    clearTimeout(this.refreshTimerHandle);
    this.refreshTimerHandle = null;
  }

  isAuthRefreshTimerRunning(): boolean {
    return !!this.refreshTimerHandle;
  }

  checkNewVersionExists(): Observable<boolean> {
    const url = `${this._config.getBaseApiUrl()}auth/ui-build-time`;
    return this._http.get<Config>(url).pipe(map((config) => {
      const isBuildTimeDifferent = config.buildTime !== this._config.getCurrentBuildTime();
      return isBuildTimeDifferent;
    }));
  }

  protected getResourceUrl(): string {
    return `${this.baseUrl}user/`;
  }

  protected getPrincipleUrl(): string {
    return `${this.principleUrl}`;
  }
}
